import { useState, useRef, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Copy, Check, ChevronDown, ChevronUp, Music, BookOpen, Play, LogOut, Sparkles, Code2, Maximize2, X, RefreshCw, ImageIcon, Search, Pencil, Upload, ExternalLink, Plus } from 'lucide-react';
import { useNavigate, Link } from '@tanstack/react-router';
import { useGeneration } from '@/hooks/use-generation';
import { usePromptEnhance } from '@/hooks/use-prompt-enhance';
import { useImageAnalysis } from '@/hooks/use-image-analysis';
import { useUsage } from '@/hooks/use-usage';
import { useSession } from '@/hooks/use-session';
import { useToast } from '@/hooks/use-toast';
import { RemotionPreview } from '@/components/preview/remotion-preview';
import { signOut, useSession as useAuthSession } from '@/lib/auth-client';
import { UsageDisplay } from '@/components/usage-display';
import { VersionPanel } from '@/components/version';
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputTools,
  PromptInputButton,
} from '@/components/ai-elements/prompt-input';
import { CodeBlockContent } from '@/components/ai-elements/code-block';
import { WebPreview, WebPreviewToolbar, WebPreviewBody } from '@/components/ai-elements/web-preview';

const schema = z.object({
  prompt: z.string().min(10, "At least 10 characters").max(30000),
  fps: z.number().min(24).max(60).default(30),
  width: z.number().min(100).max(4096).default(1920),
  height: z.number().min(100).max(4096).default(1080),
  durationInSeconds: z.number().min(1).max(300).default(10),
  audioUrl: z.string().url().optional().or(z.literal("")),
});

type FormData = z.infer<typeof schema>;

// Web component presets for different UI placements
const VIDEO_PRESETS = [
  {
    id: 'default',
    name: 'Default',
    description: 'Standard 16:9',
    width: 1920,
    height: 1080,
    aspectRatio: '16:9',
    icon: 'default',
  },
  {
    id: 'hero',
    name: 'Hero Section',
    description: 'Landing pages',
    width: 1920,
    height: 800,
    aspectRatio: '12:5',
    icon: 'hero',
  },
  {
    id: 'banner',
    name: 'Wide Banner',
    description: 'Announcement bars',
    width: 1920,
    height: 400,
    aspectRatio: '24:5',
    icon: 'banner',
  },
  {
    id: 'card',
    name: 'Feature Card',
    description: 'Cards & widgets',
    width: 600,
    height: 600,
    aspectRatio: '1:1',
    icon: 'card',
  },
  {
    id: 'sidebar',
    name: 'Sidebar',
    description: 'Vertical panels',
    width: 400,
    height: 600,
    aspectRatio: '2:3',
    icon: 'sidebar',
  },
  {
    id: 'inline',
    name: 'Inline Content',
    description: 'Blog & articles',
    width: 800,
    height: 600,
    aspectRatio: '4:3',
    icon: 'inline',
  },
] as const;

type PresetId = typeof VIDEO_PRESETS[number]['id'];

// Preset icon component - visual representation of aspect ratios
function PresetIcon({ type, className }: { type: string; className?: string }) {
  const baseClass = `${className} bg-slate-100 rounded-sm flex items-center justify-center p-1.5`;

  switch (type) {
    case 'default':
      return (
        <div className={baseClass}>
          <div className="w-full h-full bg-slate-300 rounded-[2px] flex items-center justify-center">
            <div className="w-2 h-2 bg-slate-400 rounded-full" />
          </div>
        </div>
      );
    case 'hero':
      return (
        <div className={baseClass}>
          <div className="w-full h-2/3 bg-slate-300 rounded-[2px]" />
        </div>
      );
    case 'banner':
      return (
        <div className={baseClass}>
          <div className="w-full h-1/3 bg-slate-300 rounded-[2px]" />
        </div>
      );
    case 'card':
      return (
        <div className={baseClass}>
          <div className="w-2/3 h-2/3 bg-slate-300 rounded-[2px]" />
        </div>
      );
    case 'sidebar':
      return (
        <div className={baseClass}>
          <div className="w-1/2 h-full bg-slate-300 rounded-[2px]" />
        </div>
      );
    case 'inline':
      return (
        <div className={baseClass}>
          <div className="w-3/4 h-1/2 bg-slate-300 rounded-[2px]" />
        </div>
      );
    default:
      return <div className={baseClass}><div className="w-full h-full bg-slate-300 rounded-[2px]" /></div>;
  }
}

function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

// User Profile component - displays avatar and name from auth session
function UserProfile() {
  const { data: session, isPending } = useAuthSession();

  if (isPending) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-slate-200 animate-pulse" />
        <div className="h-4 w-20 rounded bg-slate-200 animate-pulse" />
      </div>
    );
  }

  if (!session?.user) {
    return null;
  }

  const { user } = session;
  const initials = user.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user.email?.charAt(0).toUpperCase() || '?';

  return (
    <div className="flex items-center gap-2">
      {user.image ? (
        <img
          src={user.image}
          alt={user.name || 'User avatar'}
          className="h-8 w-8 rounded-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
          {initials}
        </div>
      )}
      <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">
        {user.name || user.email}
      </span>
    </div>
  );
}

export function CreatePage() {
  // All hooks must be called before any conditional returns
  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [showSources, setShowSources] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [codeFullscreen, setCodeFullscreen] = useState(false);
  const [codeTab, setCodeTab] = useState<'component' | 'usage'>('component');
  const [selectedPreset, setSelectedPreset] = useState<PresetId>('default');
  const [needsRegeneration, setNeedsRegeneration] = useState(false);
  const [lastGeneratedValues, setLastGeneratedValues] = useState<{
    fps: number;
    durationInSeconds: number;
    audioUrl: string;
    width: number;
    height: number;
  } | null>(null);
  const [inputMode, setInputMode] = useState<'describe' | 'image'>('describe');
  const [editedPrompt, setEditedPrompt] = useState<string>('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [, setIsRefunding] = useState(false);
  const [showPreviewSkeleton, setShowPreviewSkeleton] = useState(false);
  const [showCodeSkeleton, setShowCodeSkeleton] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [imageAnalysisProgress, setImageAnalysisProgress] = useState(0);
  const errorDismissedRef = useRef(false);
  const generateButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const generation = useGeneration();
  const promptEnhance = usePromptEnhance();
  const imageAnalysis = useImageAnalysis();
  const { data: usageData } = useUsage();
  const { toast } = useToast();

  // Route protection - check authentication
  const { data: authSession, isPending: isAuthPending } = useAuthSession();

  // Version History: session/version state from URL
  const {
    sessionId,
    selectedVersionId,
    session,
    versions,
    currentVersion,
    selectedVersion,
    selectedVersionFull,
    isEditingOldVersion,
    isLoading: isSessionLoading,
    isLoadingVersion,
    switchVersion,
    clearSession,
  } = useSession();

  // Minimum display time for skeleton states (prevents flicker)
  // Initialize as false - only show skeleton when there's actually a session loading
  const [showVersionSkeleton, setShowVersionSkeleton] = useState(false);
  const [showSessionSkeleton, setShowSessionSkeleton] = useState(false);

  // Previous Version skeleton - minimum 1 second display (only when sessionId exists)
  // Don't show if we have data from generation (new video just created)
  const versionSkeletonTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // No session = no skeleton needed
    if (!sessionId) {
      setShowVersionSkeleton(false);
      return;
    }

    // If we have data from generation, don't show skeleton (new video just created)
    if (generation.data?.data?.version) {
      if (versionSkeletonTimerRef.current) {
        clearTimeout(versionSkeletonTimerRef.current);
        versionSkeletonTimerRef.current = null;
      }
      setShowVersionSkeleton(false);
      return;
    }

    // Has session, no generation data - show skeleton while loading (reload scenario)
    if (isLoadingVersion && !showVersionSkeleton) {
      setShowVersionSkeleton(true);
      if (versionSkeletonTimerRef.current) {
        clearTimeout(versionSkeletonTimerRef.current);
        versionSkeletonTimerRef.current = null;
      }
    } else if (!isLoadingVersion && showVersionSkeleton) {
      // Delay hiding by 1 second for smooth transition
      if (!versionSkeletonTimerRef.current) {
        versionSkeletonTimerRef.current = setTimeout(() => {
          setShowVersionSkeleton(false);
          versionSkeletonTimerRef.current = null;
        }, 1000);
      }
    }

    return () => {
      if (versionSkeletonTimerRef.current) {
        clearTimeout(versionSkeletonTimerRef.current);
      }
    };
  }, [isLoadingVersion, sessionId, generation.data?.data?.version, showVersionSkeleton]);

  // Version History skeleton - minimum 1 second display (only when sessionId exists)
  // Don't show if we have data from generation (new video just created)
  const sessionSkeletonTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // No session = no skeleton needed
    if (!sessionId) {
      setShowSessionSkeleton(false);
      return;
    }

    // If we have data from generation, don't show skeleton (new video just created)
    if (generation.data?.data?.session) {
      if (sessionSkeletonTimerRef.current) {
        clearTimeout(sessionSkeletonTimerRef.current);
        sessionSkeletonTimerRef.current = null;
      }
      setShowSessionSkeleton(false);
      return;
    }

    // Has session, no generation data - show skeleton while loading (reload scenario)
    if (isSessionLoading && !showSessionSkeleton) {
      setShowSessionSkeleton(true);
      if (sessionSkeletonTimerRef.current) {
        clearTimeout(sessionSkeletonTimerRef.current);
        sessionSkeletonTimerRef.current = null;
      }
    } else if (!isSessionLoading && showSessionSkeleton) {
      // Delay hiding by 1 second for smooth transition
      if (!sessionSkeletonTimerRef.current) {
        sessionSkeletonTimerRef.current = setTimeout(() => {
          setShowSessionSkeleton(false);
          sessionSkeletonTimerRef.current = null;
        }, 1000);
      }
    }

    return () => {
      if (sessionSkeletonTimerRef.current) {
        clearTimeout(sessionSkeletonTimerRef.current);
      }
    };
  }, [isSessionLoading, sessionId, generation.data?.data?.session, showSessionSkeleton]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            window.location.href = '/';
          },
          onError: (ctx) => {
            console.error('[Auth] Logout failed:', ctx.error);
            toast({
              variant: 'destructive',
              title: 'Logout failed',
              description: 'Unable to sign out. Please try again.',
            });
            setIsLoggingOut(false);
          },
        },
      });
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      toast({
        variant: 'destructive',
        title: 'Logout failed',
        description: 'An unexpected error occurred. Please try again.',
      });
      setIsLoggingOut(false);
    }
  };

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      prompt: '',
      fps: 30,
      width: 1920,
      height: 1080,
      durationInSeconds: 10,
      audioUrl: '',
    },
  });

  // Core generation logic - shared between validated and bypass modes
  const submitGeneration = (data: { prompt: string; fps: number; width: number; height: number; durationInSeconds: number; audioUrl?: string }) => {
    // Reset error state for new generation
    errorDismissedRef.current = false;
    setIsRefunding(false);
    setPreviewError(null);
    setShowErrorModal(false);

    // Determine which version to edit from
    // Priority: selected version > current version > none (new session)
    // This ensures EVERY prompt within a session is treated as an edit
    const editFromVersionId = selectedVersionId ?? session?.currentVersionId ?? undefined;

    // Build strict video settings to inject into prompt
    // This ensures the AI strictly follows all selected settings
    const audioLine = data.audioUrl
      ? `- Audio: Include audio from URL "${data.audioUrl}" using Remotion's <Audio> component.\n`
      : `- Audio: No audio required.\n`;

    const videoRequirements = `\n\n[STRICT VIDEO REQUIREMENTS - MANDATORY, DO NOT IGNORE]\n` +
      `These settings are NON-NEGOTIABLE. You MUST use these exact values:\n\n` +
      `DIMENSIONS:\n` +
      `- Width: ${data.width}px (EXACT)\n` +
      `- Height: ${data.height}px (EXACT)\n` +
      `- The composition MUST be ${data.width}×${data.height} pixels. No other size is acceptable.\n\n` +
      `TIMING:\n` +
      `- Frame rate: ${data.fps} FPS (use fps={${data.fps}} in composition)\n` +
      `- Duration: ${data.durationInSeconds} seconds (${data.durationInSeconds * data.fps} total frames)\n` +
      `- durationInFrames={${data.durationInSeconds * data.fps}}\n\n` +
      `AUDIO:\n` +
      audioLine +
      `\nREMINDER: These values are set by the user and MUST be respected exactly as specified.`;

    // Append video requirements to the prompt
    const enhancedPrompt = data.prompt + videoRequirements;

    generation.mutate({
      prompt: enhancedPrompt,
      userPrompt: data.prompt, // Original user prompt for display/storage
      sessionId: sessionId,
      // ALWAYS edit if we have a session and a version to edit from
      fromVersionId: editFromVersionId,
      isEdit: !!editFromVersionId,  // True if we have a version to edit from
      forceNew: false,  // Never force new unless user clicks "New Video"
      preferences: {
        fps: data.fps,
        width: data.width,
        height: data.height,
        durationInSeconds: data.durationInSeconds,
        ...(data.audioUrl && { audioUrl: data.audioUrl }),
      },
    });
  };

  // Validated submission - for new generations or prompt changes
  const onSubmit = form.handleSubmit((data) => {
    submitGeneration(data);
  });

  // Settings-only regeneration - bypasses prompt validation, uses existing prompt
  const handleSettingsRegenerate = () => {
    // Get the existing prompt from version history or use current form value
    const existingPrompt = selectedVersionFull?.prompt ?? currentVersion?.prompt ?? form.getValues('prompt');

    if (!existingPrompt || existingPrompt.length < 3) {
      // Fallback to normal validation if no existing prompt
      onSubmit();
      return;
    }

    const currentValues = form.getValues();
    submitGeneration({
      ...currentValues,
      prompt: existingPrompt,
    });
  };

  // Get the code to display: prioritize selectedVersionFull (when viewing any version in history),
  // then latest generation result, then currentVersion
  const displayCode = selectedVersionFull?.code
    ?? generation.data?.data?.version?.code
    ?? currentVersion?.code
    ?? '';

  const displayComponentName = selectedVersionFull?.componentName
    ?? generation.data?.data?.version?.componentName
    ?? currentVersion?.componentName
    ?? '';

  // Display preferences from the current/selected version (NOT form values)
  // These are used for preview and display - form values are "proposed" changes
  const displayVersion = selectedVersionFull ?? generation.data?.data?.version ?? currentVersion;
  const displayPreferences = {
    fps: displayVersion?.preferences?.fps ?? 30,
    width: displayVersion?.preferences?.width ?? 1920,
    height: displayVersion?.preferences?.height ?? 1080,
    durationInSeconds: displayVersion?.preferences?.durationInSeconds ?? 10,
  };

  const copyCode = () => {
    if (displayCode) {
      navigator.clipboard.writeText(displayCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle preview error - refund credit and show modal
  // Memoized to prevent unnecessary re-renders in RemotionPreview
  const handlePreviewError = useCallback(async (error: string) => {
    console.error('[Preview] Error:', error);
    setPreviewError(error);

    // Only show modal if user hasn't dismissed it
    if (!errorDismissedRef.current) {
      setShowErrorModal(true);
    }

    // Only refund if we haven't already
    setIsRefunding((currentIsRefunding) => {
      if (!currentIsRefunding) {
        // Perform refund asynchronously
        fetch('/api/generate/report-failure', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: `preview_error: ${error}` }),
        })
          .then((response) => response.json())
          .then((data) => console.log('[Preview] Refund result:', data))
          .catch((err) => console.error('[Preview] Refund failed:', err));
        return true;
      }
      return currentIsRefunding;
    });
  }, []);

  const handlePreviewSuccess = useCallback(() => {
    setPreviewError(null);
    setShowErrorModal(false);
    setIsRefunding(false);
    errorDismissedRef.current = false;
  }, []);

  const closeErrorModal = () => {
    errorDismissedRef.current = true;
    setShowErrorModal(false);
  };

  // Has result if we have code from generation OR from session/version OR from selected version
  const hasResult = generation.data?.data?.version?.code || selectedVersionFull?.code || currentVersion?.code;

  // Watch for changes that require regeneration (FPS, Duration, Audio, Dimensions)
  const watchedFps = form.watch('fps');
  const watchedDuration = form.watch('durationInSeconds');
  const watchedAudio = form.watch('audioUrl');
  const watchedWidth = form.watch('width');
  const watchedHeight = form.watch('height');
  const watchedPrompt = form.watch('prompt');

  // Compare current values with last generated values
  useEffect(() => {
    if (!lastGeneratedValues) return;

    const hasChanged =
      watchedFps !== lastGeneratedValues.fps ||
      watchedDuration !== lastGeneratedValues.durationInSeconds ||
      (watchedAudio || '') !== lastGeneratedValues.audioUrl ||
      watchedWidth !== lastGeneratedValues.width ||
      watchedHeight !== lastGeneratedValues.height;

    if (hasChanged) {
      setNeedsRegeneration(true);
      generateButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      setNeedsRegeneration(false);
    }
  }, [watchedFps, watchedDuration, watchedAudio, watchedWidth, watchedHeight, lastGeneratedValues]);

  // Save values when generation succeeds
  useEffect(() => {
    if (generation.isSuccess && generation.data?.data?.version?.code) {
      setLastGeneratedValues({
        fps: form.getValues('fps'),
        durationInSeconds: form.getValues('durationInSeconds'),
        audioUrl: form.getValues('audioUrl') || '',
        width: form.getValues('width'),
        height: form.getValues('height'),
      });
      setNeedsRegeneration(false);
    }
  }, [generation.isSuccess, generation.data?.data?.version?.code]);

  // Sync form values with the selected version's preferences
  // This runs whenever user switches to a different version in Version History
  useEffect(() => {
    // Get the version to display (selected version takes priority over current)
    const displayVersion = selectedVersionFull ?? currentVersion;
    if (!displayVersion?.preferences) return;

    const prefs = displayVersion.preferences;

    // Update lastGeneratedValues to enable settings change detection
    setLastGeneratedValues({
      fps: prefs.fps ?? 30,
      durationInSeconds: prefs.durationInSeconds ?? 10,
      audioUrl: '', // Audio URL not stored in preferences
      width: prefs.width ?? 1920,
      height: prefs.height ?? 1080,
    });

    // Sync form values to match the displayed version's settings
    form.setValue('fps', prefs.fps ?? 30);
    form.setValue('durationInSeconds', prefs.durationInSeconds ?? 10);
    form.setValue('width', prefs.width ?? 1920);
    form.setValue('height', prefs.height ?? 1080);

    // Also update the selected preset based on width/height
    const matchingPreset = VIDEO_PRESETS.find(
      p => p.width === prefs.width && p.height === prefs.height
    );
    if (matchingPreset) {
      setSelectedPreset(matchingPreset.id);
    }
  }, [selectedVersionFull?.id, currentVersion?.id, form]);

  // Navigate to new session/version URL after generation success
  useEffect(() => {
    if (generation.isSuccess && generation.data?.data) {
      const { session: newSession, version } = generation.data.data;
      navigate({
        to: '/create',
        search: {
          session: newSession.id,
          version: version.id,
        },
      });
      // Note: No manual refetch needed - TanStack Query will automatically
      // refetch when URL changes (query key includes sessionId from URL)
    }
  }, [generation.isSuccess, generation.data, navigate]);

  // Show toast on generation error
  useEffect(() => {
    if (generation.isError) {
      toast({
        variant: 'destructive',
        title: 'Generation failed',
        description: generation.error?.message || 'Unable to generate video. Please try again.',
      });
    }
  }, [generation.isError, generation.error, toast]);

  // Handle URL params for billing status (upgraded, cancelled)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle successful upgrade
    if (params.get('upgraded') === 'true') {
      toast({
        variant: 'success',
        title: 'Welcome to Pro',
        description: 'Your subscription is now active.',
      });
      // Remove query param to prevent showing again on refresh
      params.delete('upgraded');
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [toast]);

  // Show toast when subscription cancellation is detected
  useEffect(() => {
    const usage = usageData?.data;
    if (usage?.isCancelling && usage?.cancellationDate) {
      const shownKey = `cancellation_toast_shown_${usage.cancellationDate}`;
      if (!localStorage.getItem(shownKey)) {
        toast({
          title: 'Subscription scheduled for cancellation',
          description: `Pro access until ${new Date(usage.cancellationDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}.`,
        });
        localStorage.setItem(shownKey, 'true');
      }
    }
  }, [usageData?.data?.isCancelling, usageData?.data?.cancellationDate, toast]);

  // Preview progress - fills over ~90 seconds (1.5 min average)
  useEffect(() => {
    if (generation.isPending) {
      setPreviewProgress(0);
      const duration = 90000; // 90 seconds (1.5 min)
      const interval = 500; // Update every 500ms
      const increment = (95 / (duration / interval));

      const timer = setInterval(() => {
        setPreviewProgress(prev => Math.min(prev + increment, 95));
      }, interval);

      return () => {
        clearInterval(timer);
        setPreviewProgress(0);
      };
    } else {
      // Complete the progress when done
      if (previewProgress > 0) {
        setPreviewProgress(100);
        const resetTimer = setTimeout(() => setPreviewProgress(0), 500);
        return () => clearTimeout(resetTimer);
      }
    }
  }, [generation.isPending]);

  // Image analysis progress - fills over ~180 seconds (3 min average)
  useEffect(() => {
    if (imageAnalysis.isAnalyzing) {
      setImageAnalysisProgress(0);
      const totalDuration = 180000; // 180 seconds (3 min)
      const interval = 500;
      const increment = (95 / (totalDuration / interval));

      const timer = setInterval(() => {
        setImageAnalysisProgress(prev => Math.min(prev + increment, 95));
      }, interval);

      return () => {
        clearInterval(timer);
        setImageAnalysisProgress(0);
      };
    } else {
      if (imageAnalysisProgress > 0) {
        setImageAnalysisProgress(100);
        const resetTimer = setTimeout(() => setImageAnalysisProgress(0), 500);
        return () => clearTimeout(resetTimer);
      }
    }
  }, [imageAnalysis.isAnalyzing]);

  // Preview skeleton state - only show when loading EXISTING version (not during new generation)
  // New generation has its own spinner, skeleton is for viewing existing videos
  const skeletonTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const isViewingExisting = !!sessionId && !!selectedVersionId;
    const hasGenerationData = !!generation.data?.data?.version?.code;

    // NEW generation completed: data from mutation → show immediately, no skeleton
    if (hasGenerationData && showPreviewSkeleton) {
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
        skeletonTimerRef.current = null;
      }
      setShowPreviewSkeleton(false);
      return;
    }

    // RELOAD scenario: no generation data, need to fetch from server
    // Show skeleton while loading, then keep for 1 second minimum
    const shouldShowSkeleton = isViewingExisting && !hasGenerationData && (isLoadingVersion || !displayCode);

    if (shouldShowSkeleton && !showPreviewSkeleton) {
      // Show skeleton immediately when loading starts
      setShowPreviewSkeleton(true);
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
        skeletonTimerRef.current = null;
      }
    } else if (!shouldShowSkeleton && showPreviewSkeleton && displayCode) {
      // Data loaded - delay hiding skeleton by 1 second for smooth transition
      if (!skeletonTimerRef.current) {
        skeletonTimerRef.current = setTimeout(() => {
          setShowPreviewSkeleton(false);
          skeletonTimerRef.current = null;
        }, 1000);
      }
    }

    return () => {
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
      }
    };
  }, [sessionId, selectedVersionId, isLoadingVersion, displayCode, generation.data?.data?.version?.code, showPreviewSkeleton]);

  // Code skeleton state - only show when loading EXISTING version (not during new generation)
  const codeSkeletonTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const isViewingExisting = !!sessionId && !!selectedVersionId;
    const hasGenerationData = !!generation.data?.data?.version?.code;

    // NEW generation completed: data from mutation → show immediately, no skeleton
    if (hasGenerationData && showCodeSkeleton) {
      if (codeSkeletonTimerRef.current) {
        clearTimeout(codeSkeletonTimerRef.current);
        codeSkeletonTimerRef.current = null;
      }
      setShowCodeSkeleton(false);
      return;
    }

    // RELOAD scenario: no generation data, need to fetch from server
    // Show skeleton while loading, then keep for 1 second minimum
    const shouldShowCodeSkeleton = isViewingExisting && !hasGenerationData && (isLoadingVersion || !displayCode);

    if (shouldShowCodeSkeleton && !showCodeSkeleton) {
      // Show skeleton immediately when loading starts
      setShowCodeSkeleton(true);
      if (codeSkeletonTimerRef.current) {
        clearTimeout(codeSkeletonTimerRef.current);
        codeSkeletonTimerRef.current = null;
      }
    } else if (!shouldShowCodeSkeleton && showCodeSkeleton && displayCode) {
      // Data loaded - delay hiding skeleton by 1 second for smooth transition
      if (!codeSkeletonTimerRef.current) {
        codeSkeletonTimerRef.current = setTimeout(() => {
          setShowCodeSkeleton(false);
          codeSkeletonTimerRef.current = null;
        }, 1000);
      }
    }

    return () => {
      if (codeSkeletonTimerRef.current) {
        clearTimeout(codeSkeletonTimerRef.current);
      }
    };
  }, [sessionId, selectedVersionId, isLoadingVersion, displayCode, generation.data?.data?.version?.code, showCodeSkeleton]);

  // Paste event listener for image analysis
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            imageAnalysis.setImage(file);
            // Switch to image mode when pasting an image
            setInputMode('image');
            e.preventDefault();
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [imageAnalysis.setImage]);

  // Auto-analyze when image is uploaded in "From Image" mode
  useEffect(() => {
    if (inputMode === 'image' && imageAnalysis.image && !imageAnalysis.analysis && !imageAnalysis.isAnalyzing) {
      // Check limit before analyzing
      const remaining = usageData?.data?.imageAnalysesRemaining ?? 0;
      if (remaining <= 0) {
        // Clear image and show limit message via hook's error state
        imageAnalysis.clearImage();
        if (fileInputRef.current) fileInputRef.current.value = '';
        // Set error manually since we're preventing the API call
        alert('Image analysis limit reached.\n\nUpgrade to Pro ($49/mo) for:\n- 15 videos/month\n- 5 image analyses/month');
        return;
      }

      // Pass current video preferences to the analysis
      const preferences = {
        fps: form.getValues('fps'),
        width: form.getValues('width'),
        height: form.getValues('height'),
        durationInSeconds: form.getValues('durationInSeconds'),
      };
      imageAnalysis.analyze(preferences);
    }
  }, [inputMode, imageAnalysis.image, imageAnalysis.analysis, imageAnalysis.isAnalyzing, imageAnalysis.analyze, imageAnalysis.clearImage, form, usageData]);

  // Sync edited prompt with analysis result
  useEffect(() => {
    if (imageAnalysis.analysis?.prompt) {
      setEditedPrompt(imageAnalysis.analysis.prompt);
    }
  }, [imageAnalysis.analysis?.prompt]);

  // Route protection - redirect to landing page if not authenticated
  useEffect(() => {
    if (!isAuthPending && !authSession?.user) {
      navigate({ to: '/' });
    }
  }, [isAuthPending, authSession, navigate]);

  // Empty screen while checking auth - redirect happens quickly
  if (isAuthPending) {
    return <div className="h-screen bg-slate-50" />;
  }

  // Don't render if not authenticated (redirect will happen via useEffect above)
  if (!authSession?.user) {
    return null;
  }

  return (
    <div className="h-screen overflow-hidden bg-slate-50">
      {/* Subtle grid background - fixed to always cover viewport */}
      <div
        className="fixed inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: `
            linear-gradient(to right, #e2e8f0 1px, transparent 1px),
            linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Main content */}
      <div className="relative h-full flex flex-col">
        {/* Header */}
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="flex h-14 items-center justify-between px-6">
            {/* Left: Logo + Navigation */}
            <div className="flex items-center gap-6">
              <h1 className="text-lg font-semibold text-slate-900">Vemotion AI</h1>

              {/* Navigation Links */}
              <nav className="flex items-center gap-1">
                <Link
                  to="/create"
                  className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-slate-100 text-slate-900"
                >
                  Create
                </Link>
                <Link
                  to="/store"
                  className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-slate-600 hover:bg-slate-50 hover:text-slate-700"
                >
                  My Videos
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              {/* User Profile */}
              <UserProfile />
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              >
                {isLoggingOut ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4" />
                )}
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Two-column layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Form */}
          <div className="w-[420px] shrink-0 border-r border-slate-200 bg-white flex flex-col">
            <div className="flex-1 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
              {/* Header */}
              <div className="border-b border-slate-100 px-6 py-5 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-medium text-slate-900">
                    What do you want to create?
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Describe your video animation
                  </p>
                </div>
                {sessionId && (
                  <button
                    type="button"
                    onClick={() => {
                      // Clear URL params (session, version)
                      clearSession();
                      // Reset generation mutation state (clears preview, code, etc.)
                      generation.reset();
                      // Reset form to defaults
                      form.reset();
                      // Reset other UI state
                      setSelectedPreset('default');
                      setLastGeneratedValues(null);
                      setNeedsRegeneration(false);
                      setPreviewError(null);
                      setShowErrorModal(false);
                    }}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:border-slate-300"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>New</span>
                  </button>
                )}
              </div>

              {/* Form Content */}
              <div className="p-6">
                {/* Hidden file input for image upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  disabled={(usageData?.data?.imageAnalysesRemaining ?? 0) <= 0}
                  onChange={(e) => {
                    // Check limit before accepting
                    const remaining = usageData?.data?.imageAnalysesRemaining ?? 0;
                    if (remaining <= 0) {
                      e.target.value = '';
                      alert('Image analysis limit reached.\n\nUpgrade to Pro ($49/mo) for:\n- 15 videos/month\n- 5 image analyses/month');
                      return;
                    }
                    imageAnalysis.setImage(e.target.files?.[0] || null);
                    // Switch to image mode when selecting a file
                    if (e.target.files?.[0]) {
                      setInputMode('image');
                    }
                  }}
                />

                {/* Tab Selector */}
                <div className={`mb-4 flex rounded-lg border border-slate-200 p-1 bg-slate-50 transition-opacity duration-200 ${imageAnalysis.isAnalyzing ? 'opacity-60 pointer-events-none' : ''}`}>
                  <button
                    type="button"
                    onClick={() => setInputMode('describe')}
                    disabled={imageAnalysis.isAnalyzing}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                      inputMode === 'describe'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Pencil className="inline h-3.5 w-3.5 mr-1.5" />
                    Describe
                  </button>
                  <button
                    type="button"
                    onClick={() => setInputMode('image')}
                    disabled={imageAnalysis.isAnalyzing}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                      inputMode === 'image'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <ImageIcon className="inline h-3.5 w-3.5 mr-1.5" />
                    From Image
                  </button>
                </div>

                {/* Describe Mode Content */}
                {inputMode === 'describe' && (
                  <>
                    {/* Version context display - shows what created the current/selected version */}
                    {(() => {
                      // Only show skeleton if there's a session (version history exists)
                      // Show skeleton while loading version (with 2s minimum display)
                      if (sessionId && (isLoadingVersion || showVersionSkeleton)) {
                        return (
                          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 shadow-sm overflow-hidden">
                            {/* Header skeleton */}
                            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                              <div className="flex items-center gap-2">
                                <div className="h-3.5 w-3.5 rounded bg-slate-200 animate-pulse" />
                                <div className="h-3 w-24 rounded bg-slate-200 animate-pulse" />
                                <div className="h-4 w-8 rounded bg-slate-200 animate-pulse" />
                              </div>
                            </div>
                            {/* Body skeleton - 2 lines average (content can be 1-3 lines) */}
                            <div className="px-3 py-3 space-y-2">
                              <div className="h-4 w-full rounded bg-slate-200 animate-pulse" />
                              <div className="h-4 w-2/3 rounded bg-slate-200 animate-pulse" />
                            </div>
                          </div>
                        );
                      }

                      // Use selected version if available, otherwise current version
                      const version = selectedVersionFull ?? currentVersion;
                      if (!version) return null;

                      // Show editInstruction if it's an edit, otherwise show prompt
                      const userInput = version.editInstruction ?? version.prompt;

                      return (
                        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 shadow-sm overflow-hidden">
                          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <BookOpen className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-xs font-medium text-slate-500">
                                Previous Version
                              </span>
                              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                                v{version.versionNumber}
                              </span>
                            </div>
                            {isEditingOldVersion && (
                              <span className="text-[10px] text-slate-400">
                                editing from here
                              </span>
                            )}
                          </div>
                          <div className="px-3 py-3 min-h-[52px]">
                            <p className="text-sm text-slate-600 line-clamp-3 italic leading-relaxed">
                              {userInput}
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* User prompt input */}
                    <div className="mb-2">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                        <Pencil className="h-3 w-3" />
                        Your Prompt
                      </label>
                    </div>
                    <PromptInput
                      onSubmit={(message) => {
                        form.setValue('prompt', message.text);
                        // Trigger submit after setting value
                        setTimeout(() => onSubmit(), 0);
                      }}
                      className="rounded-lg border border-slate-200 bg-white shadow-sm transition-all focus-within:border-slate-300 focus-within:ring-2 focus-within:ring-slate-200"
                    >
                      <PromptInputTextarea
                        value={form.watch('prompt') || ''}
                        onChange={(e) => form.setValue('prompt', e.target.value)}
                        placeholder="A bouncing ball animation with smooth easing, gradient background transitioning from purple to blue..."
                        className="min-h-[120px] border-0 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 shadow-none"
                      />
                      <PromptInputFooter className="border-t border-slate-100 px-3 py-2">
                        <PromptInputTools>
                          <span className="text-xs text-slate-400">
                            {(form.watch('prompt') || '').length} / 30000
                          </span>
                        </PromptInputTools>
                        <PromptInputTools>
                          <PromptInputButton
                            type="button"
                            onClick={() => promptEnhance.enhance(form.getValues('prompt'))}
                            disabled={promptEnhance.isEnhancing || !form.watch('prompt')?.trim()}
                            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-all hover:bg-slate-50 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {promptEnhance.isEnhancing ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Sparkles className="h-3 w-3" />
                            )}
                            <span className="hidden sm:inline">Enhance</span>
                          </PromptInputButton>
                        </PromptInputTools>
                      </PromptInputFooter>
                    </PromptInput>
                    {form.formState.errors.prompt && (
                      <p className="mt-2 text-xs text-red-500">
                        {form.formState.errors.prompt.message}
                      </p>
                    )}

                    {/* Enhancement suggestion card */}
                    {promptEnhance.enhancedPrompt && (
                      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 shadow-sm overflow-hidden">
                        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                          <Sparkles className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs font-medium text-slate-500">
                            Enhanced Prompt
                          </span>
                        </div>
                        <div className="px-3 py-3">
                          <p className="max-h-32 overflow-y-auto text-sm leading-relaxed text-slate-600">
                            {promptEnhance.enhancedPrompt}
                          </p>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-slate-100 px-3 py-2">
                          <button
                            type="button"
                            onClick={promptEnhance.clear}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            Dismiss
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              form.setValue('prompt', promptEnhance.enhancedPrompt!);
                              promptEnhance.clear();
                            }}
                            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-800"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Enhancement error */}
                    {promptEnhance.error && (
                      <p className="mt-2 text-xs text-red-500">{promptEnhance.error}</p>
                    )}
                  </>
                )}

                {/* From Image Mode Content */}
                {inputMode === 'image' && (
                  <>
                    {/* No image: Drop zone */}
                    {!imageAnalysis.image && (
                      <div
                        onClick={() => {
                          const remaining = usageData?.data?.imageAnalysesRemaining ?? 0;
                          if (remaining <= 0) {
                            alert('Image analysis limit reached.\n\nUpgrade to Pro ($49/mo) for:\n- 15 videos/month\n- 5 image analyses/month');
                            return;
                          }
                          fileInputRef.current?.click();
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const remaining = usageData?.data?.imageAnalysesRemaining ?? 0;
                          if (remaining <= 0) {
                            alert('Image analysis limit reached.\n\nUpgrade to Pro ($49/mo) for:\n- 15 videos/month\n- 5 image analyses/month');
                            return;
                          }
                          const file = e.dataTransfer.files?.[0];
                          if (file && file.type.startsWith('image/')) {
                            imageAnalysis.setImage(file);
                          }
                        }}
                        className={`flex min-h-[160px] flex-col items-center justify-center rounded-lg border px-4 py-8 text-center transition-colors ${
                          (usageData?.data?.imageAnalysesRemaining ?? 0) <= 0
                            ? 'border-slate-100 bg-slate-50 cursor-not-allowed opacity-50'
                            : 'border-slate-200 bg-slate-50 cursor-pointer hover:border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                          <Upload className="h-5 w-5 text-slate-400" />
                        </div>
                        {(usageData?.data?.imageAnalysesRemaining ?? 0) <= 0 ? (
                          <>
                            <p className="text-sm font-medium text-slate-500">
                              Image analysis limit reached
                            </p>
                            <p className="mt-1.5 text-xs text-slate-400">
                              Upgrade to Pro for more analyses
                            </p>
                            <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2.5 text-left">
                              <p className="text-xs font-medium text-slate-600 mb-1.5">Pro Plan - $49/mo</p>
                              <ul className="space-y-0.5">
                                <li className="flex items-center gap-1.5 text-xs text-slate-500">
                                  <span className="h-1 w-1 rounded-full bg-slate-400" />
                                  15 videos/month
                                </li>
                                <li className="flex items-center gap-1.5 text-xs text-slate-500">
                                  <span className="h-1 w-1 rounded-full bg-slate-400" />
                                  5 image analyses/month
                                </li>
                              </ul>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-slate-700">
                              Drop image here or click to browse
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              PNG, JPG, WebP up to 10MB
                            </p>
                            <p className="mt-2 text-xs text-slate-400">
                              Or paste with Cmd+V
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    {/* Image uploaded but analyzing */}
                    {imageAnalysis.image && imageAnalysis.isAnalyzing && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-4 flex items-center gap-3">
                          <img
                            src={`data:${imageAnalysis.image.mimeType};base64,${imageAnalysis.image.data}`}
                            alt="Upload preview"
                            className="h-16 w-16 rounded-lg object-cover border border-slate-200"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {imageAnalysis.image.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {imageAnalysis.image.dimensions.width} x {imageAnalysis.image.dimensions.height}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-center gap-2 py-4">
                          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                          <span className="text-sm font-medium text-slate-700">
                            {imageAnalysisProgress < 20 && 'Uploading image...'}
                            {imageAnalysisProgress >= 20 && imageAnalysisProgress < 50 && 'Analyzing visual elements...'}
                            {imageAnalysisProgress >= 50 && imageAnalysisProgress < 80 && 'Extracting details...'}
                            {imageAnalysisProgress >= 80 && 'Generating prompt...'}
                          </span>
                          {/* Progress bar */}
                          <div className="w-full max-w-xs">
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                              <div
                                className="h-full rounded-full bg-slate-600 transition-all duration-500 ease-out"
                                style={{ width: `${imageAnalysisProgress}%` }}
                              />
                            </div>
                            <p className="mt-1.5 text-center text-xs text-slate-400">{Math.round(imageAnalysisProgress)}%</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Analysis complete: Show result */}
                    {imageAnalysis.image && imageAnalysis.analysis && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-sm">
                        {/* Image thumbnail and content type */}
                        <div className="mb-4 flex items-start gap-3">
                          <img
                            src={`data:${imageAnalysis.image.mimeType};base64,${imageAnalysis.image.data}`}
                            alt="Upload preview"
                            className="h-20 w-20 rounded-lg object-cover border border-slate-200"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                                {imageAnalysis.analysis.contentType.primary}
                              </span>
                              <span className="text-xs text-slate-400">
                                {Math.round(imageAnalysis.analysis.contentType.confidence * 100)}% confidence
                              </span>
                            </div>
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {imageAnalysis.image.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {imageAnalysis.image.dimensions.width} x {imageAnalysis.image.dimensions.height}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              imageAnalysis.clearImage();
                              imageAnalysis.clearAnalysis();
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                            className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Color palette */}
                        <div className="mb-4">
                          <p className="mb-1.5 text-xs font-medium text-slate-500">Detected Palette</p>
                          <div className="flex gap-1">
                            {imageAnalysis.analysis.palette.slice(0, 8).map((color, i) => (
                              <div
                                key={i}
                                className="h-7 w-7 rounded border border-slate-200"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Generated prompt (editable) */}
                        <div className="mb-4">
                          <p className="mb-1.5 text-xs font-medium text-slate-500">Generated Prompt</p>
                          <textarea
                            value={editedPrompt}
                            onChange={(e) => setEditedPrompt(e.target.value)}
                            className="w-full min-h-[100px] resize-none rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 focus:border-slate-300 focus:outline-none"
                          />
                        </div>

                        {/* Use This Prompt button */}
                        <button
                          type="button"
                          onClick={() => {
                            form.setValue('prompt', editedPrompt);
                            setInputMode('describe');
                            imageAnalysis.clearImage();
                            imageAnalysis.clearAnalysis();
                            setEditedPrompt('');
                            // Reset file input to allow selecting the same file again
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className="w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
                        >
                          Use This Prompt
                        </button>
                      </div>
                    )}

                    {/* Image uploaded but not yet analyzed (waiting state - shouldn't show long due to auto-analyze) */}
                    {imageAnalysis.image && !imageAnalysis.analysis && !imageAnalysis.isAnalyzing && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={`data:${imageAnalysis.image.mimeType};base64,${imageAnalysis.image.data}`}
                            alt="Upload preview"
                            className="h-16 w-16 rounded-lg object-cover border border-slate-200"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {imageAnalysis.image.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {imageAnalysis.image.dimensions.width} x {imageAnalysis.image.dimensions.height}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              imageAnalysis.clearImage();
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                            className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            // Check limit before analyzing
                            const remaining = usageData?.data?.imageAnalysesRemaining ?? 0;
                            if (remaining <= 0) {
                              alert('Image analysis limit reached.\n\nUpgrade to Pro ($49/mo) for:\n- 15 videos/month\n- 5 image analyses/month');
                              return;
                            }
                            const preferences = {
                              fps: form.getValues('fps'),
                              width: form.getValues('width'),
                              height: form.getValues('height'),
                              durationInSeconds: form.getValues('durationInSeconds'),
                            };
                            imageAnalysis.analyze(preferences);
                          }}
                          disabled={(usageData?.data?.imageAnalysesRemaining ?? 0) <= 0}
                          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Search className="h-4 w-4" />
                          Analyze Image
                        </button>
                      </div>
                    )}

                    {/* Image analysis error */}
                    {imageAnalysis.error && (
                      <p className="mt-2 text-xs text-red-500">
                        {typeof imageAnalysis.error === 'string'
                          ? imageAnalysis.error
                          : (imageAnalysis.error as { message?: string })?.message || 'Analysis failed'}
                      </p>
                    )}
                  </>
                )}

                {/* Settings toggle */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowSettings(!showSettings)}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    <span>Component settings</span>
                    {showSettings ? (
                      <ChevronUp className="h-4 w-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-400" />
                    )}
                  </button>

                  {showSettings && (
                    <div className="mt-3 space-y-4 rounded-lg border border-slate-200 bg-white p-4">
                      {/* Use Case Presets */}
                      <div className={`transition-opacity duration-200 ${generation.isPending || imageAnalysis.isAnalyzing ? 'opacity-60' : ''}`}>
                        <label className={`mb-2.5 block text-xs font-medium transition-colors duration-200 ${generation.isPending || imageAnalysis.isAnalyzing ? 'text-slate-400' : 'text-slate-600'}`}>
                          Where will you use this?
                        </label>
                        {/* Show skeleton while loading version data to prevent preset flash */}
                        {sessionId && (isLoadingVersion || showVersionSkeleton) ? (
                          <>
                            <div className="grid grid-cols-3 gap-2">
                              {/* First row - shorter cards (101px height) */}
                              {[1, 2, 3].map((i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-slate-200 bg-white p-2.5"
                                  style={{ height: '101px' }}
                                >
                                  <div className="mb-2 h-10 w-full rounded bg-slate-100 animate-pulse" />
                                  <div className="h-4 w-14 rounded bg-slate-200 animate-pulse" />
                                  <div className="mt-1 h-3 w-8 rounded bg-slate-100 animate-pulse" />
                                </div>
                              ))}
                              {/* Second row - taller cards (117px height) */}
                              {[4, 5, 6].map((i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-slate-200 bg-white p-2.5"
                                  style={{ height: '117px' }}
                                >
                                  <div className="mb-2 h-10 w-full rounded bg-slate-100 animate-pulse" />
                                  <div className="h-4 w-16 rounded bg-slate-200 animate-pulse" />
                                  <div className="mt-0.5 h-3 w-12 rounded bg-slate-200 animate-pulse" />
                                  <div className="mt-1 h-3 w-8 rounded bg-slate-100 animate-pulse" />
                                </div>
                              ))}
                            </div>
                            <div className="mt-2 flex justify-center">
                              <div className="h-3 w-24 rounded bg-slate-200 animate-pulse" />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={`grid grid-cols-3 gap-2 transition-opacity duration-200 ${generation.isPending || imageAnalysis.isAnalyzing ? 'opacity-50 pointer-events-none' : ''}`}>
                              {VIDEO_PRESETS.map((preset) => (
                                <button
                                  key={preset.id}
                                  type="button"
                                  disabled={generation.isPending || imageAnalysis.isAnalyzing}
                                  onClick={() => {
                                    setSelectedPreset(preset.id);
                                    form.setValue('width', preset.width);
                                    form.setValue('height', preset.height);
                                  }}
                                  className={`group relative rounded-lg border p-2.5 text-left transition-all ${
                                    selectedPreset === preset.id
                                      ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                  } disabled:cursor-not-allowed`}
                                >
                                  {/* Preview shape */}
                                  <div className="mb-2 h-10 w-full overflow-hidden rounded bg-slate-100">
                                    <PresetIcon type={preset.icon} className="h-full w-full" />
                                  </div>
                                  {/* Label */}
                                  <p className={`text-xs font-medium ${
                                    selectedPreset === preset.id ? 'text-slate-900' : 'text-slate-700'
                                  }`}>
                                    {preset.name}
                                  </p>
                                  <p className="text-[10px] text-slate-400">
                                    {preset.aspectRatio}
                                  </p>
                                  {/* Selected indicator */}
                                  {selectedPreset === preset.id && (
                                    <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-slate-900">
                                      <Check className="h-2.5 w-2.5 text-white" />
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                            {/* Current dimensions display - show version dimensions vs proposed */}
                            <p className="mt-2 text-center text-[10px] text-slate-400">
                              {displayPreferences.width} × {displayPreferences.height} px
                              {(form.watch('width') !== displayPreferences.width || form.watch('height') !== displayPreferences.height) && (
                                <span className="ml-1 font-medium text-slate-600">
                                  → {form.watch('width')} × {form.watch('height')} px
                                </span>
                              )}
                            </p>
                          </>
                        )}
                      </div>

                      {/* FPS & Duration */}
                      <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
                        <div>
                          <label className="mb-1.5 block text-xs text-slate-500">FPS</label>
                          <select
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={generation.isPending || imageAnalysis.isAnalyzing}
                            {...form.register('fps', { valueAsNumber: true })}
                          >
                            <option value={24}>24</option>
                            <option value={30}>30</option>
                            <option value={60}>60</option>
                          </select>
                          {/* Current vs proposed indicator */}
                          <p className="mt-1 text-[10px] text-slate-400">
                            {displayPreferences.fps} fps
                            {form.watch('fps') !== displayPreferences.fps && (
                              <span className="ml-1 font-medium text-slate-600">
                                → {form.watch('fps')} fps
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs text-slate-500">Duration (sec)</label>
                          <input
                            type="number"
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={generation.isPending || imageAnalysis.isAnalyzing}
                            {...form.register('durationInSeconds', { valueAsNumber: true })}
                            min={1}
                            max={60}
                          />
                          {/* Current vs proposed indicator */}
                          <p className="mt-1 text-[10px] text-slate-400">
                            {displayPreferences.durationInSeconds}s
                            {form.watch('durationInSeconds') !== displayPreferences.durationInSeconds && (
                              <span className="ml-1 font-medium text-slate-600">
                                → {form.watch('durationInSeconds')}s
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Audio URL */}
                      <div className="border-t border-slate-100 pt-4">
                        <label className="mb-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                          <Music className="h-3 w-3" />
                          Audio URL (optional)
                        </label>
                        <input
                          type="url"
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={generation.isPending || imageAnalysis.isAnalyzing}
                          {...form.register('audioUrl')}
                          placeholder="https://example.com/audio.mp3"
                        />
                        <div className="mt-2 flex items-start justify-between gap-2">
                          <p className="text-xs text-slate-400">
                            Supported: mp3, wav, m4a, aac, ogg
                          </p>
                          <a
                            href="https://developers.google.com/assistant/tools/sound-library"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex shrink-0 items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-700"
                          >
                            <span>Free sounds</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Bottom Section - Generate + Usage + Roadmap (Fixed) */}
            <div className="shrink-0 border-t border-slate-200 bg-white">
              {/* Generate Button */}
              <div className="border-b border-slate-100 px-6 py-4">
                {sessionId && (isLoadingVersion || showVersionSkeleton) ? (
                  /* Generate button skeleton */
                  <div className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-200 px-4 py-3.5 animate-pulse">
                    <div className="h-4 w-4 rounded bg-slate-300" />
                    <div className="h-4 w-20 rounded bg-slate-300" />
                  </div>
                ) : (
                  <button
                    ref={generateButtonRef}
                    type="button"
                    onClick={() => {
                      // If prompt is empty but settings changed, apply settings only
                      if (!watchedPrompt?.trim() && needsRegeneration && hasResult) {
                        handleSettingsRegenerate();
                      } else {
                        // Prompt has text - use normal generation flow
                        onSubmit();
                      }
                    }}
                    disabled={generation.isPending || imageAnalysis.isAnalyzing}
                    className={`flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-3.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-slate-800 disabled:opacity-50 disabled:shadow-none ${
                      !watchedPrompt?.trim() && needsRegeneration && hasResult ? 'ring-2 ring-slate-400 ring-offset-2' : ''
                    }`}
                  >
                    {generation.isPending || imageAnalysis.isAnalyzing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {imageAnalysis.isAnalyzing ? 'Analyzing...' : !watchedPrompt?.trim() && needsRegeneration && hasResult ? 'Applying...' : 'Generating...'}
                      </>
                    ) : !watchedPrompt?.trim() && needsRegeneration && hasResult ? (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Apply Settings
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        Generate
                      </>
                    )}
                  </button>
                )}
                {generation.isError && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <p className="text-xs text-red-600">{generation.error.message}</p>
                  </div>
                )}
              </div>

              {/* Usage Display */}
              <div className="border-b border-slate-100 px-6 py-4">
                <UsageDisplay />
              </div>

            </div>
          </div>

          {/* Right Panel - Preview (transparent to show grid) */}
          <div className="flex-1 overflow-y-auto p-8">
            {/* Empty state - only show when no result AND not generating */}
            {!hasResult && !generation.isPending && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <Play className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">
                    Your preview will appear here
                  </p>
                </div>
              </div>
            )}

            {/* Loading state - full screen when no existing result */}
            {generation.isPending && !hasResult && (
              <div className="flex h-full items-center justify-center">
                <div className="w-64 text-center">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" />
                  <p className="mt-2 text-sm font-medium text-slate-700">
                    {previewProgress < 15 && 'Preparing preview...'}
                    {previewProgress >= 15 && previewProgress < 40 && 'Searching knowledge base...'}
                    {previewProgress >= 40 && previewProgress < 70 && 'Generating code...'}
                    {previewProgress >= 70 && 'Almost ready...'}
                  </p>
                  {/* Progress bar */}
                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-slate-600 transition-all duration-500 ease-out"
                      style={{ width: `${previewProgress}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-slate-400">{Math.round(previewProgress)}%</p>
                </div>
              </div>
            )}

            {/* Result - show even while generating (with overlay) */}
            {hasResult && (
              <div className="space-y-6">
                {/* Preview - Browser Frame */}
                <WebPreview className="relative">
                  {/* Loading overlay - shown while generating with existing preview */}
                  {generation.isPending && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm">
                      <div className="w-64 text-center">
                        <Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" />
                        <p className="mt-2 text-sm font-medium text-slate-700">
                          {previewProgress < 15 && 'Preparing preview...'}
                          {previewProgress >= 15 && previewProgress < 40 && 'Compiling component...'}
                          {previewProgress >= 40 && previewProgress < 70 && 'Rendering frames...'}
                          {previewProgress >= 70 && 'Almost ready...'}
                        </p>
                        {/* Progress bar */}
                        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full rounded-full bg-slate-600 transition-all duration-500 ease-out"
                            style={{ width: `${previewProgress}%` }}
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-slate-400">{Math.round(previewProgress)}%</p>
                      </div>
                    </div>
                  )}

                  {/* Browser toolbar with traffic lights */}
                  <WebPreviewToolbar title={displayComponentName || "Video Preview"} />

                  {/* Video preview body */}
                  <WebPreviewBody className="aspect-video relative">
                    {/* Video player skeleton - shown during loading with 1s minimum display */}
                    {showPreviewSkeleton && (
                      <div className="absolute inset-0 z-[5] flex flex-col bg-slate-100 overflow-hidden">
                        {/* Main preview area with centered play button */}
                        <div className="flex-1 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-slate-200 animate-pulse" />
                        </div>
                        {/* Bottom progress bar */}
                        <div className="px-4 pb-4">
                          <div className="w-full h-1 rounded-full bg-slate-200 animate-pulse" />
                        </div>
                      </div>
                    )}
                    {/* RemotionPreview - always mounted to prevent remount flicker */}
                    <RemotionPreview
                      code={displayCode}
                      componentName={displayComponentName}
                      fps={displayPreferences.fps}
                      width={displayPreferences.width}
                      height={displayPreferences.height}
                      durationInSeconds={displayPreferences.durationInSeconds}
                      onError={handlePreviewError}
                      onSuccess={handlePreviewSuccess}
                    />
                  </WebPreviewBody>

                  {/* Stats footer */}
                  <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                    <span className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{generation.data?.data?.version?.generationTimeMs ?? currentVersion?.generationTimeMs}ms</span>
                      <span className="h-1 w-1 rounded-full bg-slate-300" />
                      <span>{(generation.data?.data?.version?.tokensTotal ?? currentVersion?.tokensTotal)?.toLocaleString()} tokens</span>
                      {form.getValues('audioUrl') && (
                        <>
                          <span className="h-1 w-1 rounded-full bg-slate-300" />
                          <span className="inline-flex items-center gap-1">
                            <Music className="h-3 w-3" />
                            Audio
                          </span>
                        </>
                      )}
                    </span>
                    {/* Sources - minimal inline */}
                    {(generation.data?.data?.version?.citations ?? currentVersion?.citations) &&
                      (generation.data?.data?.version?.citations ?? currentVersion?.citations)!.length > 0 && (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowSources(!showSources)}
                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
                          >
                            <BookOpen className="h-3 w-3" />
                            <span>{(generation.data?.data?.version?.citations ?? currentVersion?.citations)!.length} sources</span>
                          </button>
                          {showSources && (
                            <div className="absolute right-0 top-6 z-[9999] w-80 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                              <ul className="space-y-2">
                                {(generation.data?.data?.version?.citations ?? currentVersion?.citations)!.map(
                                  (
                                    citation: {
                                      source: string;
                                      relevance: number;
                                      snippet: string;
                                    },
                                    index: number
                                  ) => (
                                    <li
                                      key={index}
                                      className="rounded-md border border-slate-100 px-2.5 py-2 hover:bg-slate-50"
                                    >
                                      <p className="text-xs font-medium text-slate-700">
                                        {citation.source
                                          .replace(/\.md$/, '')
                                          .split('-')
                                          .filter((part, i, arr) => arr.indexOf(part) === i)
                                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                          .join(' ')}
                                      </p>
                                      {citation.snippet && (
                                        <p className="mt-1 text-xs text-slate-500 line-clamp-2">
                                          {citation.snippet}
                                        </p>
                                      )}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </WebPreview>

                {/* Version Panel - Simplified vertical timeline */}
                {/* Skeleton while session data is loading (with 2s minimum display) */}
                {sessionId && (isSessionLoading || showSessionSkeleton) && (
                  <div className="h-[291px] rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-28 rounded bg-slate-200 animate-pulse" />
                        <div className="h-5 w-16 rounded-full bg-slate-200 animate-pulse" />
                      </div>
                      <div className="h-5 w-5 rounded bg-slate-200 animate-pulse" />
                    </div>
                    <div className="border-t border-slate-100 px-4 py-3">
                      <div className="space-y-2">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 p-3">
                            <div className="flex items-center gap-3">
                              <div className="h-5 w-8 rounded bg-slate-200 animate-pulse" />
                              <div className="h-4 w-48 rounded bg-slate-200 animate-pulse" />
                            </div>
                            <div className="h-4 w-16 rounded bg-slate-200 animate-pulse" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {/* Actual Version Panel - only show when data is loaded and skeleton timer complete */}
                {versions.length > 0 && !isSessionLoading && !showSessionSkeleton && (
                  <VersionPanel
                    versions={versions}
                    currentVersionId={session?.currentVersionId ?? null}
                    selectedVersionId={selectedVersion?.id ?? null}
                    onVersionSelect={switchVersion}
                    disabled={generation.isPending}
                  />
                )}

                {/* Code Section with Tabs */}
                <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                  {/* Loading overlay for code section - simple state, main progress is on preview */}
                  {generation.isPending && hasResult && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[2px]">
                      <div className="text-center">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                        <p className="mt-2 text-xs text-slate-500">Updating...</p>
                      </div>
                    </div>
                  )}
                  {/* Skeleton loading state */}
                  {showCodeSkeleton ? (
                    <>
                      {/* Skeleton Header with tabs placeholder */}
                      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="flex items-center gap-1">
                          {/* Component tab skeleton */}
                          <div className="flex items-center gap-2 rounded-lg bg-slate-200 px-3 py-2 animate-pulse">
                            <div className="h-4 w-4 rounded bg-slate-300" />
                            <div className="h-4 w-20 rounded bg-slate-300" />
                          </div>
                          {/* Usage tab skeleton */}
                          <div className="flex items-center gap-2 rounded-lg px-3 py-2">
                            <div className="h-4 w-4 rounded bg-slate-200 animate-pulse" />
                            <div className="h-4 w-12 rounded bg-slate-200 animate-pulse" />
                          </div>
                        </div>
                        {/* Action buttons skeleton */}
                        <div className="flex items-center gap-2">
                          <div className="h-9 w-9 rounded-lg bg-slate-200 animate-pulse" />
                          <div className="h-9 w-20 rounded-lg bg-slate-200 animate-pulse" />
                        </div>
                      </div>
                      {/* Skeleton code area */}
                      <div className="bg-white p-5 min-h-[300px]">
                        <div className="space-y-3">
                          {/* Simulated code lines with varying widths */}
                          <div className="h-4 w-32 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-64 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-48 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-16 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-56 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-40 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-72 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-24 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-52 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-36 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-60 rounded bg-slate-200 animate-pulse" />
                          <div className="h-4 w-44 rounded bg-slate-200 animate-pulse" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Header with tabs and buttons */}
                      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
                    {/* Tabs */}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCodeTab('component')}
                        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                          codeTab === 'component'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Code2 className="h-4 w-4" />
                        Component
                      </button>
                      <button
                        type="button"
                        onClick={() => setCodeTab('usage')}
                        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                          codeTab === 'usage'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Play className="h-4 w-4" />
                        Usage
                      </button>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {codeTab === 'component' && (
                        <button
                          type="button"
                          onClick={() => setCodeFullscreen(true)}
                          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                        >
                          <Maximize2 className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (codeTab === 'component') {
                            copyCode();
                          } else {
                            const componentName = displayComponentName || 'GeneratedComponent';
                            const fileName = toKebabCase(componentName);
                            const durationInFrames = form.getValues('durationInSeconds') * form.getValues('fps');
                            const embedCode = `import { Player } from '@remotion/player';
import { ${componentName} } from './${fileName}';

<Player
  component={${componentName}}
  durationInFrames={${durationInFrames}}
  fps={${form.getValues('fps')}}
  compositionWidth={${form.getValues('width')}}
  compositionHeight={${form.getValues('height')}}
  style={{ width: '100%' }}
  controls
/>`;
                            navigator.clipboard.writeText(embedCode);
                            setEmbedCopied(true);
                            setTimeout(() => setEmbedCopied(false), 2000);
                          }
                        }}
                        className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
                          (codeTab === 'component' && copied) || (codeTab === 'usage' && embedCopied)
                            ? 'border-green-200 bg-green-50 text-green-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                        }`}
                      >
                        {(codeTab === 'component' && copied) || (codeTab === 'usage' && embedCopied) ? (
                          <>
                            <Check className="h-4 w-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Code content */}
                  {codeTab === 'component' ? (
                    <div className="max-h-[400px] overflow-auto [&_pre]:!rounded-none [&_pre]:!border-0">
                      <CodeBlockContent
                        code={displayCode}
                        language="tsx"
                        showLineNumbers
                      />
                    </div>
                  ) : (
                    <>
                      <div className="max-h-[400px] overflow-auto [&_pre]:!rounded-none [&_pre]:!border-0">
                        <CodeBlockContent
                          code={`import { Player } from '@remotion/player';
import { ${displayComponentName || 'GeneratedComponent'} } from './${toKebabCase(displayComponentName || 'generated-component')}';

<Player
  component={${displayComponentName || 'GeneratedComponent'}}
  durationInFrames={${form.getValues('durationInSeconds') * form.getValues('fps')}}
  fps={${form.getValues('fps')}}
  compositionWidth={${form.getValues('width')}}
  compositionHeight={${form.getValues('height')}}
  style={{ width: '100%' }}
  controls
/>`}
                          language="tsx"
                          showLineNumbers
                        />
                      </div>
                      <div className="border-t border-slate-200 bg-slate-50 px-5 py-3">
                        <p className="text-xs text-slate-500">
                          Install: <code className="rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 font-mono text-slate-600">npm i @remotion/player remotion</code>
                        </p>
                      </div>
                    </>
                  )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Code Fullscreen Modal */}
      {codeFullscreen && (
        <div className="fixed inset-0 z-50 bg-slate-900/20 backdrop-blur-sm">
          <div className="flex h-full flex-col p-4 md:p-8">
            <div className="flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white">
                    <Code2 className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      {toKebabCase(displayComponentName || '')}.tsx
                    </h3>
                    <p className="text-xs text-slate-500">Remotion Component</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={copyCode}
                    className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
                      copied
                        ? 'border-green-200 bg-green-50 text-green-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy Code
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCodeFullscreen(false)}
                    className="flex items-center justify-center rounded-lg border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              {/* Modal Code Area */}
              <div className="flex-1 overflow-auto">
                <CodeBlockContent
                  code={displayCode}
                  language="tsx"
                  showLineNumbers
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 p-4">
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            {/* Icon */}
            <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-slate-50">
              <svg
                className="h-6 w-6 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="mb-2 text-center text-2xl font-semibold text-slate-900">
              Preview Error
            </h3>

            {/* Message */}
            <p className="mb-6 text-center text-sm text-slate-500">
              An error occurred while generating the video. Our team has been notified and will fix it soon.
            </p>

            {/* Refund notice */}
            <div className="mb-6 border-t border-slate-100" />
            <div className="mb-6 flex items-center justify-center gap-2">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
              <span className="text-sm text-slate-600">Your credit was not deducted</span>
            </div>

            {/* Error details (collapsible) */}
            {previewError && (
              <details className="mb-6">
                <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600">
                  Technical details
                </summary>
                <pre className="mt-2 max-h-32 overflow-auto rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
                  {previewError}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={closeErrorModal}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  closeErrorModal();
                  onSubmit();
                }}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Backward compatibility
export { CreatePage as StudioPage };
export { CreatePage as DashboardPage };
